import type { ChatAttachment } from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { IDBPDatabase, openDB } from 'idb';

const DB_NAME = 'online_community';
const DRAFT_STORE_NAME = 'chat_drafts';

export type Attachment = {
  name: string;
  type: string;
  size: number;
  blob: Blob;
  chatAttachment?: ChatAttachment;
};

export type Draft = {
  channelCode: string;
  message: string;
  attachments: Attachment[];
  updatedAt: number;
  userType?: ChannelMemberUserTypeEnum;
  userId?: number;
};

const ENCRYPTION_KEY = import.meta.env.VITE_CHAT_DRAFT_SECRET_KEY;

if (!ENCRYPTION_KEY) {
  console.error('Encryption key not found.');
}

async function encryptData(data: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(data);
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(ENCRYPTION_KEY),
      'AES-GCM',
      false,
      ['encrypt'],
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
    const encryptedArray = new Uint8Array(encrypted);
    return btoa(
      JSON.stringify({
        iv: Array.from(iv),
        data: Array.from(encryptedArray),
      }),
    );
  } catch (error) {
    console.warn('Encryption failed, storing data unencrypted:', error);
    return btoa(data);
  }
}

async function decryptData(encryptedBase64: string): Promise<string> {
  try {
    const { iv, data } = JSON.parse(atob(encryptedBase64));
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(ENCRYPTION_KEY),
      'AES-GCM',
      false,
      ['decrypt'],
    );
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(iv) },
      key,
      new Uint8Array(data),
    );
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.warn('Decryption failed, attempting to read as unencrypted:', error);
    try {
      return atob(encryptedBase64);
    } catch {
      // base64でもない場合、そのまま返す
      return encryptedBase64;
    }
  }
}

async function getDbPromise(): Promise<IDBPDatabase | null> {
  // SSR対応
  if (typeof window === 'undefined') {
    return null;
  }

  return await openDB(DB_NAME, 1, {
    upgrade(db) {
      db.createObjectStore(DRAFT_STORE_NAME, { keyPath: ['channelCode', 'userType', 'userId'] });
    },
  });
}

export async function saveChatDraft(
  channelCode: string,
  message: string,
  attachments: Attachment[] = [],
  userType?: ChannelMemberUserTypeEnum,
  userId?: number,
): Promise<void> {
  if (!channelCode || !userType || !userId) {
    return;
  }
  const db = await getDbPromise();

  // SSR対応
  if (!db) {
    return;
  }

  const draft: Draft = {
    channelCode,
    message,
    attachments,
    updatedAt: Date.now(),
    userType,
    userId,
  };

  const encrypted = await encryptData(JSON.stringify(draft));
  try {
    await db.put(DRAFT_STORE_NAME, {
      channelCode,
      userType,
      userId,
      encrypted,
    });
  } catch (error) {
    console.error('indexedDB Save Error during database operation:', error);
  }
}

export async function loadChatDraft(
  channelCode: string,
  currentUserType: ChannelMemberUserTypeEnum,
  currentUserId: number,
): Promise<Draft | undefined> {
  if (!currentUserType || !currentUserId) {
    return undefined;
  }
  const db = await getDbPromise();
  if (!db) {
    return undefined;
  }

  const record = await db.get(DRAFT_STORE_NAME, [channelCode, currentUserType, currentUserId]);

  if (!record || !record.encrypted || !record.userType || !record.userId) {
    return undefined;
  }
  try {
    const json = await decryptData(record.encrypted);
    return JSON.parse(json) as Draft;
  } catch (error) {
    console.error('indexedDB Load Error:', error);
    return undefined;
  }
}

export async function deleteChatDraft(
  channelCode: string,
  currentUserType: ChannelMemberUserTypeEnum,
  currentUserId: number,
): Promise<void> {
  const db = await getDbPromise();
  if (!db) {
    return;
  }

  await db.delete(DRAFT_STORE_NAME, [channelCode, currentUserType, currentUserId]);
}

export async function clearChatDrafts(): Promise<void> {
  const db = await getDbPromise();
  if (!db) {
    return;
  }

  await db.clear(DRAFT_STORE_NAME);
}
